import { JSONSchema7 } from 'json-schema';
import { customSchemas, types as commonTypes } from '@sitebuilder/common';
import { headingsSchema } from '../Header';
import ManagersContainer from './ManagersContainer';

const {
    schema: generatedSchema,
    uiSchema: generatedUiSchema,
    group: generatedGroup,
    dependencies: generatedDependencies,
} = customSchemas.GenerateAnalyticsSchema(['Read Bio']);

const schema: JSONSchema7 = {
    title: 'Managers Container',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        ...generatedSchema,
        containerTitles: headingsSchema,
        ...generatedSchema,
        dataKey: {
            type: 'string',
            title: 'Data key',
            default: '',
        },
        managerInfo: {
            type: 'string',
            title: 'Manager Info',
            default: '',
        },
        isCompany: {
            type: 'boolean',
            title: 'Is this a management comany',
            default: false,
        },
        inNewTab: {
            type: 'boolean',
            title: 'Open Links in New Tab',
            default: false,
        },
        linkTarget: {
            type: 'string',
            title: 'Target for <a> tag',
            description: 'Can either be _blank, _self, _parent, _top',
        },
        linkIcon: {
            type: 'string',
            title: 'Icon for link',
            default: 'fas fa-link',
        },
        isImgLink: {
            type: 'boolean',
            default: false,
            title: 'Fetch images from url',
        },
        localeSupport: {
            type: 'boolean',
            default: false,
            title: 'Is locale support required?',
        },
        teamLink: {
            type: 'string',
            default: false,
            title: 'Management Team Link',
        },
        isBioReadMore: {
            type: 'boolean',
            default: false,
            title: 'Implement read more functionality for Bios',
        },
        investmentsExperienceDetails: {
            type: 'boolean',
            default: false,
            title: 'Add investments experience',
        },
        altText: {
            type: 'string',
            default: '',
            title: 'Alternative Text',
        },
        managerBioHtml: {
            type: 'string',
            title: 'Manager Bio HTML',
            default: '',
            description: 'ex: <p>{{manager.biography}}</p>',
        },
        managerNameHtml: {
            type: 'string',
            title: 'Manager Name HTML',
            default: '',
            description: 'ex: <p>{{manager.fullName}}</p>',
        },
        groupCompany: {
            type: 'boolean',
            title: 'Group managers by Company',
            default: false,
        },
        isAccordion: {
            type: 'boolean',
            default: false,
            title: 'Apply Accordion to Manager',
        },
        isAddPlaceholder: {
            type: 'boolean',
            default: false,
            title: 'Add Placeholder for Managers',
        },
        isTransformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        disableTabStaticData: {
            type: 'boolean',
            title: 'Disable tabbing through static data',
            description: '',
        },
    },
    dependencies: {
        isImgLink: {
            oneOf: [
                {
                    properties: {
                        isImgLink: { enum: [true] },
                        imageLink: {
                            type: 'string',
                            title: 'Base url for the images',
                        },
                        imageType: {
                            type: 'string',
                            title: 'Image Type',
                            description: 'Ex: jpg or jpeg or png',
                        },
                    },
                    required: ['imageLink'],
                },
            ],
        },
        investmentsExperienceDetails: {
            oneOf: [
                {
                    properties: {
                        investmentsExperienceDetails: { enum: [true] },
                        investmentsDetails: {
                            type: 'string',
                            title: 'Investments experience details',
                        },
                    },
                },
            ],
        },
        isBioReadMore: {
            oneOf: [
                {
                    properties: {
                        isBioReadMore: { enum: [true] },
                        readMoreText: {
                            type: 'string',
                            title: 'Read More Text',
                        },
                        readLessText: {
                            type: 'string',
                            title: 'Read Less Text',
                        },
                        numberOfCharacters: {
                            type: 'number',
                            title: 'Number of Characters to show for bios',
                            default: 150,
                        },
                    },
                    required: ['readMoreText', 'readLessText', 'numberOfCharacters'],
                },
            ],
        },
        isAccordion: {
            oneOf: [
                {
                    properties: {
                        isAccordion: { enum: [true] },
                        accordionIconType: {
                            type: 'string',
                            title: 'Accordion Icon type',
                            enum: ['FontAwesome Icon', 'Image'],
                            default: 'FontAwesome Icon',
                        },
                    },
                },
            ],
        },
        accordionIconType: {
            oneOf: [
                {
                    properties: {
                        accordionIconType: { enum: ['FontAwesome Icon'] },
                        accordionIconOpenName: {
                            type: 'string',
                            title: 'Accordion Icon Open Name',
                            description: 'Icon Name',
                            default: 'fa-chevron-up',
                        },
                        accordionIconClosingName: {
                            type: 'string',
                            title: 'Accordion Icon Closing Name',
                            description: 'Icon Name',
                            default: 'fa-chevron-down',
                        },
                        openFirstAccordion: {
                            type: 'boolean',
                            default: false,
                            title: 'Open first accordion by default',
                        },
                    },
                },
                {
                    properties: {
                        accordionIconType: { enum: ['Image'] },
                        accordionIconOpenImage: {
                            type: 'string',
                            title: 'Accordion Icon Open Image',
                            description: 'Icon Image',
                        },
                        accordionIconClosingImage: {
                            type: 'string',
                            title: 'Accordion Icon Closing Image',
                            description: 'Icon Image',
                        },
                        openFirstAccordion: {
                            type: 'boolean',
                            default: false,
                            title: 'Open first accordion by default',
                        },
                    },
                },
            ],
        },
        isTransformData: {
            oneOf: [
                {
                    properties: {
                        isTransformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default:
                                // eslint-disable-next-line max-len
                                'return (data) => { return { } }',
                        },
                    },
                },
                {
                    properties: {
                        isTransformData: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
        ...generatedDependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'containerTitles',
            'dataKey',
            'managerInfo',
            'managerNameHtml',
            'managerBioHtml',
            'isCompany',
            'inNewTab',
            'linkTarget',
            'linkIcon',
            'isImgLink',
            'imageLink',
            'imageType',
            'localeSupport',
            'teamLink',
            'investmentsExperienceDetails',
            'investmentsDetails',
            'isBioReadMore',
            'readMoreText',
            'readLessText',
            'numberOfCharacters',
            'altText',
            'groupCompany',
            'companyName',
            'isAccordion',
            'accordionIconType',
            'accordionIconOpenName',
            'accordionIconClosingName',
            'accordionIconOpenImage',
            'accordionIconClosingImage',
            'openFirstAccordion',
            'isAddPlaceholder',
        ],
        ...generatedGroup,
        'Transform Data': ['isTransformData', 'functionToTransform'],
        Accessibility: ['disableTabStaticData'],
    },
];

const ManagersContainerWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        managerInfo: {
            'ui:widget': 'ResourcePicker',
        },
        readMoreText: {
            'ui:widget': 'ResourcePicker',
        },
        investmentsDetails: {
            'ui:widget': 'ResourcePicker',
        },
        readLessText: {
            'ui:widget': 'ResourcePicker',
        },
        containerTitles: {
            items: {
                headerText: {
                    'ui:widget': 'ResourcePicker',
                },
            },
        },
        isCompany: {
            'ui:widget': 'CustomCheckbox',
        },
        inNewTab: {
            'ui:widget': 'CustomCheckbox',
        },
        isImgLink: {
            'ui:widget': 'CustomCheckbox',
        },
        investmentsExperienceDetails: {
            'ui:widget': 'CustomCheckbox',
        },
        isBioReadMore: {
            'ui:widget': 'CustomCheckbox',
        },
        localeSupport: {
            'ui:widget': 'CustomCheckbox',
        },
        numberOfCharacters: {
            'ui:widget': 'CustomTextInput',
        },
        dataKey: {
            'ui:widget': 'CustomTextInput',
        },
        linkTarget: {
            'ui:widget': 'CustomTextInput',
        },
        imageLink: {
            'ui:widget': 'CustomTextInput',
        },
        altText: {
            'ui:widget': 'CustomTextInput',
        },
        teamLink: {
            'ui:widget': 'CustomTextInput',
        },
        linkIcon: {
            'ui:widget': 'CustomTextInput',
        },
        imageType: {
            'ui:widget': 'ResourcePicker',
        },
        managerBioHtml: {
            'ui:widget': 'ResourcePicker',
        },
        managerNameHtml: {
            'ui:widget': 'ResourcePicker',
        },
        groupCompany: {
            'ui:widget': 'CustomCheckbox',
        },
        isAccordion: {
            'ui:widget': 'CustomCheckbox',
        },
        accordionIconType: {
            'ui:widget': 'CustomSelect',
        },
        accordionIconOpenName: {
            'ui:widget': 'CustomTextInput',
        },
        accordionIconClosingName: {
            'ui:widget': 'CustomTextInput',
        },
        accordionIconOpenImage: {
            'ui:widget': 'MediaPicker',
        },
        accordionIconClosingImage: {
            'ui:widget': 'MediaPicker',
        },
        openFirstAccordion: {
            'ui:widget': 'CustomCheckbox',
        },
        isAddPlaceholder: {
            'ui:widget': 'CustomCheckbox',
        },
        isTransformData: { 'ui:widget': 'CustomCheckbox' },
        functionToTransform: { 'ui:widget': 'JSEditor' },
        ...generatedUiSchema,
        disableTabStaticData: { 'ui:widget': 'CustomCheckbox' },
    },
    templateView: ManagersContainer,
    id: 'ManagersContainer',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default ManagersContainerWidget;
